import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import { useStateValue } from "../context/StateProvider";
import { actionType } from "../context/reducer";
import {
  listenListingLeads,
  listenBuyerLeads,
  subscribeOpenHouses,
  getDeletedDeals,
  getDeletedListingLeads,
  getDeletedBuyerLeads,
} from "../utils/firebasefunctions";
import { selectSearchResults } from "../utils/globalSearch";
import { formatDate } from "../utils/helper";

const NO_DELETED = { deals: [], listing: [], buyer: [] };

export default function GlobalSearch() {
  const [{ user, deals, listingLeads, buyerLeads, openHouses }, dispatch] =
    useStateValue();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [deleted, setDeleted] = useState(NO_DELETED);
  const askedForDeleted = useRef(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  // Leads and open houses are NOT loaded globally — the pipelines, status
  // sheet and lead tracker each subscribe locally, so on a fresh login this
  // box would find no leads at all. Subscribe here (as the Sidebar already
  // does) and push into app state so everyone else benefits too.
  useEffect(() => {
    if (!user) return undefined;
    const unsubL = listenListingLeads((rows) =>
      dispatch({ type: actionType.STORE_LISTING_LEADS, payload: rows || [] }),
    );
    const unsubB = listenBuyerLeads((rows) =>
      dispatch({ type: actionType.STORE_BUYER_LEADS, payload: rows || [] }),
    );
    const unsubO = subscribeOpenHouses((rows) =>
      dispatch({ type: actionType.STORE_OPEN_HOUSES, payload: rows || [] }),
    );
    return () => {
      unsubL && unsubL();
      unsubB && unsubB();
      unsubO && unsubO();
    };
  }, [user, dispatch]);

  // Soft-deleted records can NEVER arrive through the subscriptions above:
  // listenListingLeads, listenBuyerLeads and getAlldealforAdmin all strip
  // `deletedAt > 0` before the rows reach app state. That filtering is right
  // for the pipelines and the board, but it is exactly why a deleted record is
  // invisible everywhere — the 96 Orange Ave W problem, which cost a database
  // key to solve. So fetch the deleted ones separately.
  //
  // Lazily, once, on the first real search: they change rarely, and nobody who
  // never opens this box pays for the read. Failure is non-fatal — live
  // results still work, we just cannot offer the deleted ones.
  // Guard on unmount, NOT on this effect re-running. The effect depends on
  // `query`, so it re-runs on every keystroke — a per-run cancelled flag would
  // have the second keystroke throw away the fetch the first one started, and
  // the deleted group would stay permanently empty while the fetch itself
  // looked like it fired correctly.
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );

  useEffect(() => {
    if (!user || !query.trim() || askedForDeleted.current) return;
    askedForDeleted.current = true;
    Promise.all([
      getDeletedDeals(),
      getDeletedListingLeads(),
      getDeletedBuyerLeads(),
    ])
      .then(([d, listing, buyer]) => {
        if (mounted.current) {
          setDeleted({
            deals: d || [],
            listing: listing || [],
            buyer: buyer || [],
          });
        }
      })
      .catch((err) => {
        console.warn("Deleted records unavailable for search:", err);
      });
  }, [user, query]);

  // Close dropdown on outside click.
  useEffect(() => {
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, []);

  // Cmd/Ctrl+K focuses the search.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Deleted rows ride in on the same arrays as the live ones. selectSearchResults
  // reads each row's own deletedAt, so it sorts them into the "Deleted" group and
  // routes them to Recently Deleted without needing to know where they came from.
  // They also inherit the same per-agent visibility rules as live leads.
  const groups = useMemo(
    () =>
      selectSearchResults({
        query,
        deals: [...(deals || []), ...deleted.deals],
        listingLeads: [...(listingLeads || []), ...deleted.listing],
        buyerLeads: [...(buyerLeads || []), ...deleted.buyer],
        openHouses,
        user,
      }),
    [query, deals, listingLeads, buyerLeads, openHouses, user, deleted],
  );

  // Flat list backs arrow-key navigation across group boundaries.
  const flat = useMemo(() => groups.flatMap((g) => g.results), [groups]);

  const go = (row) => {
    if (!row) return;
    navigate(row.route);
    setQuery("");
    setOpen(false);
    inputRef.current?.blur();
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, flat.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(flat[activeIdx]);
    } else if (e.key === "Escape") {
      setQuery("");
      setOpen(false);
      inputRef.current?.blur();
    }
  };

  // Rows are rendered inside their groups but numbered as one flat list, so
  // the arrow keys can walk straight across a group heading.
  let running = -1;

  return (
    <div ref={wrapRef} className="relative w-36 sm:w-56 lg:w-72">
      <div className="relative">
        <Search
          size={14}
          className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
        />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setActiveIdx(0);
          }}
          onFocus={() => query && setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Search everything…"
          className="w-full bg-slate-50 border border-slate-200 focus:bg-white focus:border-slate-400 rounded-md pl-8 pr-7 py-1.5 text-[13px] text-slate-900 placeholder:text-slate-400 outline-none transition"
        />
        {query && (
          <button
            type="button"
            onClick={() => {
              setQuery("");
              setOpen(false);
              inputRef.current?.focus();
            }}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5"
            aria-label="Clear search"
          >
            <X size={13} />
          </button>
        )}
      </div>
      {open && query && (
        <div className="absolute left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg overflow-hidden z-50 max-h-96 overflow-y-auto">
          {!groups.length ? (
            <div className="px-3 py-2.5 text-[12.5px] text-slate-500">
              Nothing matches "{query}"
            </div>
          ) : (
            groups.map((g) => (
              <div key={g.key}>
                <div className="px-3 py-1.5 bg-slate-50 text-[11px] text-slate-500 sticky top-0">
                  {g.label}
                  {g.total > g.results.length ? ` (${g.total})` : ""}
                </div>
                <ul>
                  {g.results.map((row) => {
                    running += 1;
                    const idx = running;
                    return (
                      <li
                        key={`${row.type}-${row.id}`}
                        onMouseEnter={() => setActiveIdx(idx)}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          go(row);
                        }}
                        className={`px-3 py-2 cursor-pointer flex items-center justify-between gap-3 ${
                          idx === activeIdx ? "bg-slate-100" : "hover:bg-slate-50"
                        }`}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="text-[13px] font-medium text-slate-900 truncate">
                            {row.title}
                          </div>
                          <div className="text-[11px] text-slate-500 truncate">
                            {row.subtitle}
                          </div>
                        </div>
                        <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 shrink-0">
                          {/* The group heading already says "Deleted", so the
                              badge carries the more useful fact: when. */}
                          {row.deleted ? formatDate(row.deletedAt) : row.badge}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
